import type { EnrichedPublicShop } from '../../../stores/shopStore'
import { getCatStyle } from '../categoryStyles'
import type { FloorLayout, MallLayout, StorefrontSlot } from './types'

/**
 * Concourse geometry (world units = metres), taken from real shopping-centre
 * tenant design criteria so the space reads at the scale a person expects:
 *   - 6 m tenant bay, the standard in-line small-shop module (5.3–6.0 m)
 *   - 13 m concourse, mid-range for a two-sided mall run
 *   - 3.6 m storefront height (12'-0" AFF, the near-universal lease-line height)
 *   - a 1.15 m fascia/bulkhead above it carrying the tenant's sign
 *   - flat soffit at 4.75 m, opening into a glazed barrel vault peaking at 7.5 m
 * Kept here (not scattered across the scene builders) so layout, architecture
 * and collision always agree on where things are.
 */
export const SLOT_WIDTH = 6
export const CORRIDOR_HALF_WIDTH = 6.5
export const CORRIDOR_MARGIN = 5
export const END_MARGIN = 8
/** Depth of the entrance apron in front of the first bay (positive Z). */
export const ENTRY_ZONE = 7.5

export const STOREFRONT_HEIGHT = 3.6
export const FASCIA_HEIGHT = 1.15
export const SOFFIT_HEIGHT = STOREFRONT_HEIGHT + FASCIA_HEIGHT
export const VAULT_HALF_WIDTH = 4.2
export const VAULT_RISE = 2.8
/** "Neutral pier" — the landlord-owned divider between adjacent tenants. */
export const PIER_WIDTH = 0.34

/**
 * Where the wing directory stands, relative to the concourse centreline, and
 * how it faces. Shared with the spawn point below so you arrive looking at it
 * rather than having to turn round and find it.
 */
export const DIRECTORY_PYLON = { dx: -2.6, z: 0.5, rotY: 0.59 }

export const FLOOR_GAP = 30
export const FLOOR_SPACING = CORRIDOR_HALF_WIDTH * 2 + FLOOR_GAP

const UNCATEGORIZED_ID = 'uncategorized'

/**
 * Groups shops by category into floors and lays out storefronts as a two-sided
 * corridor per floor. Pure function — no React, no three.js — so it can be
 * sanity-checked in isolation before any rendering exists.
 */
export function computeMallLayout(shops: EnrichedPublicShop[]): MallLayout {
  const byCategory = new Map<string, EnrichedPublicShop[]>()
  const uncategorized: EnrichedPublicShop[] = []

  shops.forEach((shop) => {
    const cat = shop.category?.trim()
    if (cat) {
      if (!byCategory.has(cat)) byCategory.set(cat, [])
      byCategory.get(cat)!.push(shop)
    } else {
      uncategorized.push(shop)
    }
  })

  const categoryNames = Array.from(byCategory.keys()).sort((a, b) => a.localeCompare(b))

  const floors: FloorLayout[] = categoryNames.map((category, floorIndex) =>
    buildFloor(category, category, byCategory.get(category)!, floorIndex)
  )

  if (uncategorized.length > 0) {
    floors.push(buildFloor(UNCATEGORIZED_ID, 'Uncategorized', uncategorized, floors.length))
  }

  return { floors, totalShops: shops.length }
}

function buildFloor(id: string, category: string, shops: EnrichedPublicShop[], floorIndex: number): FloorLayout {
  const style = getCatStyle(id === UNCATEGORIZED_ID ? undefined : category)
  const sideCount = Math.max(1, Math.ceil(shops.length / 2))
  const corridorLength = CORRIDOR_MARGIN + sideCount * SLOT_WIDTH + END_MARGIN
  const originX = floorIndex * FLOOR_SPACING

  const slots: StorefrontSlot[] = shops.map((shop, i) => {
    const side: 'left' | 'right' = i % 2 === 0 ? 'left' : 'right'
    const slotIndex = Math.floor(i / 2)
    const localX = side === 'left' ? -CORRIDOR_HALF_WIDTH : CORRIDOR_HALF_WIDTH
    // Bays tile back-to-back from the concourse mouth; centre the slot in its bay.
    const localZ = -CORRIDOR_MARGIN - slotIndex * SLOT_WIDTH - SLOT_WIDTH / 2
    // Storefronts face inward, toward the concourse centreline.
    const rotationY = side === 'left' ? Math.PI / 2 : -Math.PI / 2

    return {
      shop,
      side,
      slotIndex,
      position: { x: originX + localX, y: 0, z: localZ },
      rotationY,
    }
  })

  return {
    id,
    category,
    color: style.color,
    rgb: style.rgb,
    floorIndex,
    corridorLength,
    originX,
    // You arrive on the entrance apron beside the directory, turned just enough
    // that the board and the concourse beyond it are both in the first frame.
    // (yaw 0 == looking down -Z; positive yaw turns left, toward the directory.)
    entry: { x: originX + 1.2, z: 6.2, yaw: 0.34 },
    slots,
    shopCount: shops.length,
  }
}
